/**
 * useSuspendStaff — TanStack mutation over
 * `POST /admin/staff/:userId/suspend` (no body).
 *
 * Error envelope (tagged union):
 *   - 'cannotSuspendSelf' — 422 (a root cannot suspend their own account).
 *   - 'notFound'          — 404 (no such staff member).
 *   - 'forbidden'         — 403 (caller is not root).
 *   - 'error'             — anything else.
 *
 * On success, invalidates the canonical staff list key so the row's
 * status flips to `suspended`.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { STAFF_LIST_QUERY_KEY } from './use-staff-list.js';

export type SuspendStaffError =
  | { readonly kind: 'cannotSuspendSelf' }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error' };

export interface UseSuspendStaffResult {
  readonly mutateAsync: (userId: string) => Promise<null>;
  readonly isPending: boolean;
}

export function useSuspendStaff(): UseSuspendStaffResult {
  const queryClient = useQueryClient();

  // Note: `useMutation` does not accept `void` as its first generic
  // because lint forbids `void` outside return types. Use `null` and
  // resolve with it on success — call sites await without consuming
  // the value.
  const mutation = useMutation<null, SuspendStaffError, string>({
    mutationFn: async (userId) => {
      const { response } = await apiClient.POST(
        '/admin/staff/{userId}/suspend',
        { params: { path: { userId } } },
      );
      if (response.status === 422) {
        throw { kind: 'cannotSuspendSelf' } satisfies SuspendStaffError;
      }
      if (response.status === 404) {
        throw { kind: 'notFound' } satisfies SuspendStaffError;
      }
      if (response.status === 403) {
        throw { kind: 'forbidden' } satisfies SuspendStaffError;
      }
      if (!response.ok) {
        throw { kind: 'error' } satisfies SuspendStaffError;
      }
      return null;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: STAFF_LIST_QUERY_KEY });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
